import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('user-token');
    if (!token) {
      setLoading(false);
      return;
    }
    api.getMe()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem('user-token');
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await api.login(email, password);
    localStorage.setItem('user-token', result.token);
    setUser(result.user);
    return result.user;
  }, []);

  const register = useCallback(async (name, email, password, opts = {}) => {
    const result = await api.register(name, email, password, opts);
    if (result.needsVerification) {
      return { needsVerification: true, message: result.message };
    }
    if (result.token) {
      localStorage.setItem('user-token', result.token);
      setUser(result.user);
    }
    return result.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch (_) { /* ignore */ }
    localStorage.removeItem('user-token');
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    const updated = await api.updateMe(data);
    setUser((prev) => ({ ...prev, ...updated }));
    return updated;
  }, []);

  const deleteAccount = useCallback(async () => {
    await api.deleteMyAccount();
    localStorage.removeItem('user-token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, deleteAccount, isLoggedIn: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
